import {
  createDefaultFilterSet,
  createFilterSet,
  FilterSetBase,
  SearchQuery,
  SearchResult,
  SearchTemplate,
  SearchView,
  FilterSet,
  FilterInstance,
} from '../../../generic-search-common/src'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useGetSearchResult } from './use-api'
import { RequestOptions } from '@msaf/generic-search-common'

// Props that are common between the table and the map interface for advanced search
export interface CommonAdvancedSearchProps {
  searchTypeKey: string
  requestOptions: RequestOptions
  dateFormat?: string
  searchTemplate?: SearchTemplate
  pageSize?: number
  onSearchQueryChange?: (query: SearchQuery) => void
  filterBy?: FilterSetBase
  viewKey?: string
  cachedSearchQuery?: SearchQuery
}

export interface UseSearchProps {
  searchTemplate?: SearchTemplate
  viewKey?: string
  pageSize?: number
  requestOptions: RequestOptions
  queryParamViewKey?: string
  onSearchQueryChange?: (query: SearchQuery) => void
  filterBy?: FilterSetBase
  cachedSearchQuery?: SearchQuery
}

export type QueryParamSearchType = {
  searchTypeKey: string
  filters: FilterSetBase
}

export const getSearchTemplateWithDisplayFilters = (searchTemplate: SearchTemplate) => {
  // Filter out the hidden filters so that the rest of the logic need not worry about it
  const searchFilters = searchTemplate.filters.filter((f) => f.type !== 'hidden')
  searchTemplate.filters = searchFilters
  return searchTemplate
}

export function useSearch({
  searchTemplate,
  onSearchQueryChange,
  pageSize,
  viewKey,
  requestOptions,
  filterBy,
  cachedSearchQuery,
}: UseSearchProps) {
  const navigate = useNavigate()
  const { search } = useLocation()
  const [searchResults, setSearchResults] = useState<SearchResult>()
  const [currentView, setCurrentView] = useState<SearchView>()
  // `searchQuery` includes the filters that have been applied through the UI
  const [searchQuery, setSearchQuery] = useState<SearchQuery>()
  // `effectiveSearchQuery` is the search query used to fetch the results
  // It includes the filters that have been applied through the UI and
  // any filter(s) that must be applied to further filter the results.
  const [effectiveSearchQuery, setEffectiveSearchQuery] = useState<SearchQuery>()

  const {
    isLoading: isSearching,
    isSuccess,
    data,
  } = useGetSearchResult(effectiveSearchQuery, requestOptions, !!effectiveSearchQuery)

  useEffect(() => {
    if (isSuccess && data?.searchResult) {
      setSearchResults(data?.searchResult)
    }
  }, [isSuccess, searchQuery?.filterSet, data?.searchResult])

  useEffect(() => {
    const getDefaultQuery = (searchTemplate: SearchTemplate) => {
      let filterSet: FilterSetBase | undefined
      // Check if the url has search filters
      const queryParamSearchFilters = new URLSearchParams(search).get('queryParamSearchFilters')
      if (queryParamSearchFilters) {
        const { searchTypeKey, filters } = JSON.parse(queryParamSearchFilters) as QueryParamSearchType
        // Check if the search type key passed in params match the current search template
        // This helps in preventing passibg 'Bad filter' to the server
        if (searchTypeKey === searchTemplate.searchTypeKey) {
          filterSet = filters
        }
      }

      if (!filterSet) {
        const defaultFilters: (FilterSet | FilterInstance)[] = createDefaultFilterSet(searchTemplate.filters)
        const defaultFilterSet = defaultFilters.length > 0 && createFilterSet(defaultFilters, 'AND')
        const filter = defaultFilterSet || createFilterSet([], 'AND')
        filterSet = filter.filterSet
      }

      const currentViewKey = viewKey || searchTemplate.views[0].viewKey
      return {
        viewKey: currentViewKey,
        searchTypeKey: searchTemplate.searchTypeKey,
        requestedPage: 1,
        requestedPageLength: pageSize,
        filterSet,
      }
    }

    if (searchTemplate) {
      if (cachedSearchQuery) {
        // Set the cached query as the search query.
        setSearchQuery(cachedSearchQuery)
      } else {
        const defaultQuery = getDefaultQuery(searchTemplate)
        setSearchQuery(defaultQuery)
      }
      setCurrentView(searchTemplate.views[0])
    }
    // Excluded `search` and `cachedSearchQuery` to prevent additional re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTemplate, viewKey, pageSize])

  // When the searchQuery changes
  useEffect(() => {
    if (searchQuery) {
      // Perform additional operations on the search query like caching the search query
      if (onSearchQueryChange) {
        onSearchQueryChange(searchQuery)
      }

      // Add the search filters to the url as query params, if enabled
      if (searchTemplate?.allowsQueryParamFiltering) {
        const appliedFilters = searchQuery.filterSet
        const currentSearchTypeKey = searchQuery.searchTypeKey
        const filterString = JSON.stringify({
          searchTypeKey: currentSearchTypeKey,
          filters: appliedFilters,
        })
        const params = new URLSearchParams()
        if (appliedFilters?.filters.length) {
          // Set the url
          params.append('queryParamSearchFilters', filterString)
          navigate({ search: params.toString() })
        } else {
          // Reset the url
          params.delete('queryParamSearchFilters')
          navigate({ search: '' })
        }
      }

      // Update the effective search query that will eventually be used for the search
      let effectiveSearchQuery: SearchQuery = {
        ...searchQuery,
        viewKey: viewKey || searchQuery.viewKey,
      }

      // Update the requested page length in case of map mode, so that the `searchQuery`
      // always uses the pageSize from props
      if (searchTemplate?.hasMapMode) {
        effectiveSearchQuery = {
          ...effectiveSearchQuery,
          requestedPageLength: pageSize,
        }
      }

      // If a `filterBy` filter set is present, we add it to the existing search query filters
      if (filterBy) {
        // Add the filters passed in to the applied filters
        const effectiveFilters = [...searchQuery.filterSet.filters, ...filterBy.filters]
        const effectiveFilterSet = { ...searchQuery.filterSet, filters: effectiveFilters }
        effectiveSearchQuery = {
          ...effectiveSearchQuery,
          filterSet: effectiveFilterSet,
        }
      }

      setEffectiveSearchQuery(effectiveSearchQuery)
    }
    // Excluded `filterBy`and `onSearchQueryChange` object from the dependency list to prevent unnecessary re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery])

  async function updateQuery(newQuery: SearchQuery, resetToFirstPage?: boolean) {
    const query: SearchQuery = { ...searchQuery, ...newQuery }

    if (resetToFirstPage) {
      query.requestedPage = 1
    }

    setSearchQuery(query)
  }

  async function setSortColumn(orderColumn: string, orderDirection: 'asc' | 'desc' = 'asc') {
    if (!searchQuery) {
      throw new Error('searchQuery must be set to provide sort results')
    }
    setSearchQuery({
      ...searchQuery,
      ordering: [
        {
          elementKey: orderColumn,
          direction: orderDirection,
        },
      ],
    })
  }

  async function setViewKey(viewKey: string) {
    if (!searchQuery) {
      throw new Error('searchQuery must be set to change the view')
    }

    const view = searchTemplate?.views.find((v) => v.viewKey === viewKey)

    if (view == null) {
      throw new Error(`viewKey ${viewKey} does not correspond to a valid view`)
    }

    if (searchQuery.viewKey !== viewKey) {
      setSearchQuery({
        ...searchQuery,
        viewKey,
        requestedPage: 1,
      })

      setCurrentView(view)
    }
  }

  async function setCurrentPage(page: number) {
    if (!searchQuery) {
      throw new Error('searchQuery must be set to change the page')
    }

    let requestedPage = page
    // Default to last page if requested page greater than last page
    if (searchResults?.totalRows && pageSize) {
      const lastPage = Math.ceil(searchResults?.totalRows / pageSize)
      if (requestedPage > lastPage) {
        requestedPage = lastPage
      }
    }

    setSearchQuery({
      ...searchQuery,
      requestedPage,
    })
  }

  return {
    // Query modifiers
    updateQuery,
    setSearchQuery,
    setCurrentPage,
    setSortColumn,
    setViewKey,

    // State
    isSearching,
    searchResults,
    searchQuery: searchQuery || effectiveSearchQuery,
    currentView,
    searchTemplate,
  }
}
